// @flow

import React from "react";
import { localize } from "redux-i18n";
import { Button, Form, FormGroup, Label } from "@components";
import { reduxForm, Field } from "redux-form";
import type { FormProps } from "redux-form";
import { DatePickerFormWrapper, Dropdown } from "@components/form";
import type { PropertiesSchema } from "@types";
import { getSharedEmployeePositions } from "@modules/shared/selectors/employee-positions";
import { connect } from "react-redux";
import type { State } from "@store/reducers";
import { getAbsencesFilters } from "@modules/shared/selectors/employee-absences";
import config from "@config";
import actionCreators from "@modules/shared/actionCreator";
import type { EmployeeType } from "@modules/employee/types";

type PropsFromDispatch = $Exact<{
  exportEmployeeAbsenceHistory: typeof actionCreators.ExportEmployeeAbsenceHistory,
}>;
type PropsFromState = $Exact<{
  getSharedEmployeePositions: Object,
  getAbsencesFilters: Object,
}>;

type Props = $Exact<{
  clientId: number,
  employee: EmployeeType,
  schema: ?PropertiesSchema,
  canAddAbsence?: boolean,
  onSubmit: () => void,
  onExport: () => void,
  showAddAbsenceModal: () => void,
  addButtonText: string,
  ...PropsFromDispatch,
  ...PropsFromState,
  ...PropsFromLocalize,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
}>;

export class AbsencesFilter extends React.Component<Props> {
  get absenceTypeOptions() {
    if (!this.props.schema) {
      return [];
    }
    //TODO: the absence types values are fetch from the schema, use existing endpoint
    const absenceTypes =
      (((this.props.schema || {}).properties || {}).absenceType || {}).oneOf || [];

    return absenceTypes.map(item => ({
      value: item.const,
      label: item.title,
    }));
  }

  get employeePositions() {
    return [
      {
        label: this.props.t("HIDE"),
        value: false,
      },
      {
        label: this.props.t("SHOW"),
        value: true,
      },
    ];
  }

  handleExportReport = () => {
    if (this.props.onExport) {
      this.props.onExport();
    } else {
      this.props.exportEmployeeAbsenceHistory({
        clientId: this.props.clientId,
        employee: this.props.employee,
      });
    }
  };

  render() {
    return (
      <div className="d-flex align-items-end">
        <Form onSubmit={this.props.handleSubmit} className="d-flex align-items-end flex-1">
          <div className="d-flex">
            <FormGroup className="p-0 m-0 mr-3">
              <Label for="startsAt">{this.props.t("DATE_FROM")}</Label>
              <Field
                name="startsAt"
                id="startsAt"
                component={DatePickerFormWrapper}
                customProps={{ dateFormat: config.dateFormatBE, id: "startsAt" }}
              />
            </FormGroup>

            <FormGroup className="p-0 m-0 mr-3">
              <Label for="rtwAt">{this.props.t("DATE_TO")}</Label>
              <Field
                name="rtwAt"
                id="rtwAt"
                component={DatePickerFormWrapper}
                customProps={{ dateFormat: config.dateFormatBE, id: "rtwAt" }}
              />
            </FormGroup>

            <FormGroup className="p-0 m-0 mr-3">
              <Label for="absenceType">{this.props.t("ABSENCE_TYPE")}</Label>
              <Field
                id="absenceType"
                name="absenceType"
                component={Dropdown}
                options={this.absenceTypeOptions}
              />
            </FormGroup>

            <FormGroup className="p-0 m-0 mr-3">
              <Label for="isCancelled">{this.props.t("CANCELLED_ABSENCES")}</Label>
              <Field
                id="isCancelled"
                name="isCancelled"
                component={Dropdown}
                props={{
                  value: (this.props.getAbsencesFilters || {}).isCancelled || false,
                }}
                options={this.employeePositions}
              />
            </FormGroup>
          </div>

          <Button materialIcon="replay_circle_filled">{this.props.t("SUBMIT")}</Button>
        </Form>
        <div className="d-flex align-items-end">
          {this.props.canAddAbsence && (
            <Button
              materialIcon="add_box"
              className="ml-3 align-self-baseline"
              onClick={this.props.showAddAbsenceModal}
            >
              {this.props.t(this.props.addButtonText || "ADD")}
            </Button>
          )}

          <Button
            materialIcon="file_download"
            className="ml-3 align-self-baseline"
            onClick={this.handleExportReport}
          >
            {this.props.t("EXPORT")}
          </Button>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps: PropsFromDispatch = {
  exportEmployeeAbsenceHistory: actionCreators.ExportEmployeeAbsenceHistory,
};

const mapStateToProps = (state: State): PropsFromState => ({
  getSharedEmployeePositions: getSharedEmployeePositions(state),
  getAbsencesFilters: getAbsencesFilters(state),
});

export default reduxForm({
  form: "absences-filters",
})(localize()(connect(mapStateToProps, mapDispatchToProps)(AbsencesFilter)));
